import React, { useState } from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import round from 'lodash/round';
import { FormattedMessage, FormattedDate, FormattedTime, useIntl, defineMessages } from 'react-intl';

import env from 'Env';
import { manualSync, setSettings } from 'Redux/ducks/steam';
import MessageBox from 'Components/Utility/MessageBox';

import 'Components/Account/ContactPreferences/PreferenceCenter.scss';

const STEAM_SYNC_DOWN = false;
const MAX_WISHLIST = 3500;
const MAX_OWNED = 5000;

const messages = defineMessages({
  lastSyncError: {
    id: 'steam-sync.lastSyncError',
    defaultMessage: 'Your last sync {relativeTime} was not successful. Please check your Steam privacy settings or try again later.',
  },
  lastSyncError2: {
    id: 'steam-sync.lastSyncError2',
    defaultMessage: 'My Profile and Game Details must be set to Public',
  },
  overWishlistLimitError: {
    id: 'steam-sync.overWishlistLimitError',
    defaultMessage: 'Your steam wishlist is too big, we can only sync up to {limit} games',
  },
  overOwnedLimitError: {
    id: 'steam-sync.overOwnedLimitError',
    defaultMessage: 'Your steam library is too big, we can only sync up to {limit} owned games',
  },
  steamSettingsLink: {
    id: 'steam-sync.steam-settings-button',
    defaultMessage: 'Click here to change your Steam privacy settings',
  },
  steamDown: {
    id: 'steam-sync.steam-down',
    defaultMessage: 'We are currently experiencing problems connecting to steam. Please try again later. If you have your steam sync settings enabled we will automatically try again for you',
  },
});

export default function SteamSync({ steamId, syncSettings, setError, setSuccess }) {
  const [tempSettings, setTempSettings] = useState({
    wishlistSync: syncSettings.wishlistSync || false,
    wishlistRemoveOwned: syncSettings.wishlistRemoveOwned || false,
    wishlistRemoveRemoved: syncSettings.wishlistRemoveRemoved || false,
    ownedSync: syncSettings.ownedSync || false,
  });

  const [hasUpdated, setHasUpdated] = useState(false);

  const { formatMessage, formatRelativeTime } = useIntl();

  const setSetting = (event) => {
    const field = event.target.id;
    const checked = event.target.checked;

    if (field === 'wishlistSync') {
      setTempSettings({
        ...tempSettings,
        [field]: checked,
        wishlistRemoveOwned: checked,
        wishlistRemoveRemoved: checked });
    } else {
      setTempSettings({ ...tempSettings, [field]: checked });
    }

    setHasUpdated(true);
  };

  const doSave = (settings) => {
    setSettings(settings)
      .then(() => {
        setSuccess('settings');
        setHasUpdated(false);
      })
      .catch(setError);
  };

  const doManualSync = () => {
    manualSync()
      .then((res) => {
        const err = get(res, 'steam.syncSettings.lastSyncError');
        if (!err) {
          setSuccess('sync');
        }
      })
      .catch(setError);
  };

  const reset = () => {
    const offSettings = {
      wishlistSync: false,
      wishlistRemoveOwned: false,
      wishlistRemoveRemoved: false,
      ownedSync: false,
    };

    setTempSettings(offSettings);
    return doSave(offSettings);
  };

  const lastSyncError = get(syncSettings, 'lastSyncError');

  const showLastSync = (syncSettings.wishlistSync || syncSettings.ownedSync) &&
    (syncSettings.lastAutoSync || syncSettings.lastManualSync) && !lastSyncError;

  const canManualSync =
    (syncSettings.wishlistSync || syncSettings.ownedSync) &&
    (env.isDevelopment ||
      lastSyncError ||
      !syncSettings.lastManualSync ||
      new Date(syncSettings.lastManualSync) < new Date(Date.now() - (60 * 60 * 24 * 1000)));

  const lastSyncToShow = syncSettings.lastAutoSync > syncSettings.lastManualSync ?
    syncSettings.lastAutoSync : syncSettings.lastManualSync;

  const lastSyncRelative = lastSyncToShow && (new Date() - new Date(lastSyncToShow)) / 1000;
  let lastSyncRelativeAdjusted = lastSyncRelative;

  let lastSyncRelativeUnit = 'second';

  if (lastSyncRelative > 60 * 2) {
    lastSyncRelativeAdjusted = lastSyncRelative / 60;
    lastSyncRelativeUnit = 'minute';
  }

  if (lastSyncRelative > 60 * 60 * 2) {
    lastSyncRelativeAdjusted = lastSyncRelative / (60 * 60);
    lastSyncRelativeUnit = 'hour';
  }

  if (lastSyncRelative > 60 * 60 * 24 * 2) {
    lastSyncRelativeAdjusted = lastSyncRelative / (60 * 60 * 24);
    lastSyncRelativeUnit = 'day';
  }

  const showError = () => {
    if (!lastSyncError) {
      return null;
    }

    if (STEAM_SYNC_DOWN) {
      return (
        <div className="last-sync mb-5">
          <MessageBox
            message={formatMessage(messages.steamDown)}
            type="error"
          />
        </div>
      );
    }

    if (lastSyncError.includes('Over maximum steam wishlist size')) {
      return (
        <div className="last-sync mb-5">
          <MessageBox
            message={formatMessage(messages.overWishlistLimitError, { limit: MAX_WISHLIST })}
            type="warning"
          />
        </div>
      );
    }

    if (lastSyncError.includes('Over maximum steam owned size')) {
      return (
        <div className="last-sync mb-5">
          <MessageBox
            message={formatMessage(messages.overOwnedLimitError, { limit: MAX_OWNED })}
            type="warning"
          />
        </div>
      );
    }


    return (
      <div className="last-sync mb-5">
        <MessageBox
          message={`${formatMessage(messages.lastSyncError, { relativeTime: `(${formatRelativeTime(0 - round(lastSyncRelativeAdjusted), lastSyncRelativeUnit)})` })}<br/><br/><strong>${formatMessage(messages.lastSyncError2)}</strong><br/><br/><a href="https://steamcommunity.com/profiles/${steamId}/edit/settings" target="_blank" rel="noreferrer"><strong>${formatMessage(messages.steamSettingsLink)}</strong></a>`}
          type="error"
        />
      </div>
    );
  };

  return (
    <div className="steam-sync account-preference-center">
      <p className="sync-info">
        <FormattedMessage id="steam-sync.sync-info" defaultMessage="We will automagically sync your wishlist, according to your preferences below, once a week. You can manually sync once every 24 hours. Fanatical does not have access to modify your Steam Wishlist." />
      </p>
      {showLastSync &&
        <div className="last-sync mb-3">
          <FormattedMessage
            id="steam-sync.lastSync"
            defaultMessage="Your last sync was on {date} at {time}"
            values={{
              date: <FormattedDate
                value={lastSyncToShow}
                year="numeric"
                month="long"
                day="2-digit"
              />,
              time: <FormattedTime
                value={lastSyncToShow}
              />,
            }}
          />
        </div>
      }
      {canManualSync &&
        <Button
          color="primary"
          className="btn-account mb-4"
          disabled={!canManualSync}
          onClick={doManualSync}
        >
          {lastSyncError ?
            <FormattedMessage id="steam-sync.manual-sync-button-again" defaultMessage="Retry Sync Now" />
            :
            <FormattedMessage id="steam-sync.manual-sync-button" defaultMessage="Sync Now" />
          }
        </Button>
      }
      { showError() }
      <h3>
        <FormattedMessage id="steam-sync.header" defaultMessage="Steam Wishlist Sync Settings" />
      </h3>
      <div className="setting-line-item-container">
        <div className="setting-line-item">
          <div className="setting-line-item-title">
            <FormattedMessage id="steam-sync.wishlistSync.title" defaultMessage="Wishlist Sync" />
          </div>
          <div className="setting-line-item-details">
            <div className="setting-line-item-data">
              <div className="setting-line-item-data-smallprint">
                <FormattedMessage id="steam-sync.wishlistSync.desc" defaultMessage="We will scan your Steam Wishlist and add any of those games that are available on Fanatical, to your Fanatical Wishlist also." />
              </div>
            </div>
            <div className="setting-line-item-link">
              <div className="switch-container">
                <label className="switch" htmlFor="wishlistSync">
                  <input
                    id="wishlistSync"
                    name="wishlistSync"
                    type="checkbox"
                    checked={tempSettings.wishlistSync || false}
                    className="ml-1"
                    onChange={setSetting}
                  />
                  <span className="theme-slider round" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="setting-line-item">
          <div className="setting-line-item-title">
            <FormattedMessage id="steam-sync.ownedSync.title" defaultMessage="Sync your Steam Library" />
          </div>
          <div className="setting-line-item-details">
            <div className="setting-line-item-data">
              <div className="setting-line-item-data-smallprint">
                <FormattedMessage id="steam-sync.ownedSync.desc" defaultMessage="We will scan your Steam Library, and use this information to avoid recommending games to you that you already own on Steam" />
              </div>
            </div>
            <div className="setting-line-item-link">
              <div className="switch-container">
                <label className="switch" htmlFor="ownedSync">
                  <input
                    id="ownedSync"
                    name="ownedSync"
                    type="checkbox"
                    checked={tempSettings.ownedSync || false}
                    className="ml-1"
                    onChange={setSetting}
                  />
                  <span className="theme-slider round" />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!!tempSettings.wishlistSync &&
        <>
          <div className="setting-line-item-container">
            <div className="setting-line-item">
              <div className="setting-line-item-title">
                <FormattedMessage id="steam-sync.wishlistRemoveOwned.title" defaultMessage="Removed Owned Games" />
              </div>
              <div className="setting-line-item-details">
                <div className="setting-line-item-data">
                  <div className="setting-line-item-data-smallprint">
                    <FormattedMessage id="steam-sync.wishlistRemoveOwned.desc" defaultMessage="We will scan your Steam Library, and remove any games you already own on Steam from your Fanatical Wishlist" />
                  </div>
                </div>
                <div className="setting-line-item-link">
                  <div className="switch-container">
                    <label className="switch" htmlFor="wishlistRemoveOwned">
                      <input
                        id="wishlistRemoveOwned"
                        name="wishlistRemoveOwned"
                        type="checkbox"
                        checked={tempSettings.wishlistRemoveOwned || false}
                        className="ml-1"
                        onChange={setSetting}
                      />
                      <span className="theme-slider round" />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="setting-line-item-container">
            <div className="setting-line-item">
              <div className="setting-line-item-title">
                <FormattedMessage id="steam-sync.wishlistRemoveRemoved.title" defaultMessage="Remove when removed on Steam" />
              </div>
              <div className="setting-line-item-details">
                <div className="setting-line-item-data">
                  <div className="setting-line-item-data-smallprint">
                    <FormattedMessage id="steam-sync.wishlistRemoveRemoved.desc" defaultMessage="If you add a game to your Fanatical Wishlist via Steam Sync, then later remove it from your Steam Wishlist, we will also remove it from your Fanatical Wishlist" />
                  </div>
                </div>
                <div className="setting-line-item-link">
                  <div className="switch-container">
                    <label className="switch" htmlFor="wishlistRemoveRemoved">
                      <input
                        id="wishlistRemoveRemoved"
                        name="wishlistRemoveRemoved"
                        type="checkbox"
                        checked={tempSettings.wishlistRemoveRemoved || false}
                        className="ml-1"
                        onChange={setSetting}
                      />
                      <span className="theme-slider round" />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      }
      <div className="btn-account-group mt-4">
        <div className="btn-account-container btn-account-container-buttons">
          <div>
            <Button
              color="primary"
              className="btn-account"
              disabled={!hasUpdated}
              onClick={() => doSave(tempSettings)}
            >
              <FormattedMessage id="steam-sync.save-button" defaultMessage="Save Sync Settings" />
            </Button>
          </div>
          <div>
            <Button
              color="secondary"
              tag={'a'}
              className="btn-account"
              onClick={reset}
            >
              <FormattedMessage id="steam-sync.disable-button" defaultMessage="Disable Steam Sync" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

SteamSync.propTypes = {
  syncSettings: PropTypes.shape({
    wishlistSync: PropTypes.bool,
    wishlistRemoveOwned: PropTypes.bool,
    wishlistRemoveRemoved: PropTypes.bool,
    ownedSync: PropTypes.bool,
  }),
  setError: PropTypes.func.isRequired,
  steamId: PropTypes.string.isRequired,
};

SteamSync.defaultProps = {
  syncSettings: {
    wishlistSync: false,
    wishlistRemoveOwned: false,
    wishlistRemoveRemoved: false,
    ownedSync: false,
  },
};
